// THE TOUR ENTRY POINT — how a page asks for a guided (spotlight) tour, and how the overlay that
// shows it plugs in. The page never draws the overlay; the shell does. The contract:
//
//   launchTour(tour)        a page (Guides "Launch tour") asks for a tour. It dispatches the
//                           cancelable window event TOUR_LAUNCH_EVENT ('guides:tour:launch') with
//                           the Tour as `detail`, and returns true when a listener took it.
//   onTourLaunch(handler)   the overlay subscribes (once, at the shell). It receives every Tour and
//                           takes it (the event is marked handled). Returns the unsubscribe.
//   tourFromGuide(guide)    a guide's sections as tour steps: one step per section, its heading and
//                           Markdown body; a section names what to spotlight with a directive line
//                           `<!-- tour: <anchor> [<path>] -->` (anchor = a `data-tour` value on the
//                           page, path = the route to open first, made a route of the host by its `routeOf`).
//
// A step's anchor is a CSS-free name: the overlay looks up `[data-tour="<anchor>"]` (nav items,
// pages and chips already carry them). The shell's TourHost subscribes and runs it in the Spotlight.

export const TOUR_LAUNCH_EVENT = 'guides:tour:launch';

export interface TourStep {
  /** Stable within the tour (the guide section's id). */
  id: string;
  title: string;
  /** Markdown, without the tour directive. */
  body: string;
  /** The `data-tour` value to spotlight; null: a step with no target (centred). */
  anchor: string | null;
  /** The route to open before the step (the host's); null: stay where you are. */
  path: string | null;
}

export interface Tour {
  /** `guide:<scope>/<key>`: one id per tour, for "resume" and analytics. */
  id: string;
  title: string;
  source: { kind: 'guide'; scope: string; key: string };
  steps: TourStep[];
  /** The step to start at (0-based). */
  start: number;
}

/** `<!-- tour: nav-board /board -->` on a line of its own. */
export const TOUR_DIRECTIVE_RE = /^[ \t]*<!--\s*tour:\s*([A-Za-z0-9_.:-]+)(?:\s+(\/\S*))?\s*-->[ \t]*$/m;

/** The Markdown without its tour directive lines (for reading, previews and the PDF). */
export function stripTourDirectives(markdown: string): string {
  return markdown
    .split('\n')
    .filter((row) => !new RegExp(TOUR_DIRECTIVE_RE.source).test(row))
    .join('\n')
    .replace(/^\n+/, '');
}

/** Where a tour runs: the app's scope (the tour's id) and how a written path becomes a route of the host. */
export interface TourPlace {
  scope: string;
  routeOf?: (path: string) => string;
}

export function tourStepOf(
  section: { id: number | string; heading: string; body: string },
  place: TourPlace,
): TourStep {
  const m = TOUR_DIRECTIVE_RE.exec(section.body);
  const path = m?.[2] ? (place.routeOf ? place.routeOf(m[2]) : m[2]) : null;
  return {
    id: String(section.id),
    title: section.heading,
    body: stripTourDirectives(section.body),
    anchor: m?.[1] ?? null,
    path,
  };
}

export function tourFromGuide(
  guide: { key: string; title: string; sectionList: { id: number; heading: string; body: string }[] },
  place: TourPlace,
  start = 0,
): Tour {
  const steps = guide.sectionList.map((s) => tourStepOf(s, place));
  return {
    id: `guide:${place.scope}/${guide.key}`,
    title: guide.title,
    source: { kind: 'guide', scope: place.scope, key: guide.key },
    steps,
    start: Math.max(0, Math.min(start, steps.length - 1)),
  };
}

/** Asks the shell's overlay to run the tour. True when a listener took it. */
export function launchTour(tour: Tour): boolean {
  if (typeof window === 'undefined' || !tour.steps.length) return false;
  const ev = new CustomEvent<Tour>(TOUR_LAUNCH_EVENT, { detail: tour, cancelable: true });
  window.dispatchEvent(ev);
  return ev.defaultPrevented;
}

/** The overlay's subscription: it receives every launched tour and takes it. */
export function onTourLaunch(handler: (tour: Tour) => void): () => void {
  const listener = (e: Event) => {
    e.preventDefault();
    handler((e as CustomEvent<Tour>).detail);
  };
  window.addEventListener(TOUR_LAUNCH_EVENT, listener);
  return () => window.removeEventListener(TOUR_LAUNCH_EVENT, listener);
}
